import React from "react";
import Dropzone, { type DropzoneRef } from "react-dropzone";
import { defaultStyles } from "react-file-icon";

import type { FellowAttachment } from ".";
import { faFile, faFilePdf, faFilm, faPlusCircle, faSpinner } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faTrashCan } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { Map } from "immutable";
import { debounce, isEqual, memoize, noop } from "lodash";

import { yesno } from "~/lib/prompts";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { isZoom } from "~/lib/utils";
import { SingleName } from "~/ui/avatar/NameList";

import { defaultThumbBounds, openLightbox, type PSUIOptions } from "../imageViewer";

import styles from "./css/attachments.module.css";

export const getIconProps = function (filename: string) {
	const extension = filename.split(".").pop() || "";
	const defaults = defaultStyles[extension] || {};
	return {
		...defaults,
		extension,
	};
};

export function fileTypeIcon(type: string) {
	if (type.startsWith("video")) {
		return faFilm;
	}
	switch (type.toLowerCase()) {
		case "application/pdf":
			return faFilePdf;
		default:
			return faFile;
	}
}

function onAttachmentClick() {
	dispatchAnalyticsTrack("[1on1s] download attachment");
}

type AttachmentFigureProps = React.PropsWithChildren<{
	readonly?: boolean;
	onDeleteClick?: () => void;
	loading?: boolean;
	attachment?: { id: string; filename: string; dimensions?: { height?: number; width?: number } };
}>;

function AttachmentFigure({ children, onDeleteClick, loading, attachment, readonly = true }: AttachmentFigureProps) {
	const onDragStart = React.useCallback(
		(e: React.DragEvent<HTMLElement>) => {
			if (!attachment) return;
			e.dataTransfer.clearData("Files");
			e.dataTransfer.setData("fellow/attachment", JSON.stringify(attachment));
		},
		[attachment],
	);
	return (
		<figure
			className={styles.attachmentItem}
			data-attachment-id={attachment?.id}
			onClick={onAttachmentClick}
			draggable={true}
			onDragStart={onDragStart}
		>
			{!readonly && onDeleteClick && (
				<button title="Delete this attachment" className={styles.deleteButton} onClick={onDeleteClick}>
					<FontAwesomeIcon icon={faTrashCan} fixedWidth />
				</button>
			)}
			{children}
			{loading && (
				<div className={cx(styles.preview, styles.uploadingPreview)}>
					<FontAwesomeIcon icon={faSpinner} pulse className={styles.spinner} />
				</div>
			)}
		</figure>
	);
}

function AttachmentCaption({ text }: { text?: string }) {
	return (
		<figcaption className={styles.caption} title={text}>
			{text ?? <>&nbsp;</>}
		</figcaption>
	);
}

function ImageAttachmentFigure({
	src,
	alt,
	caption,
	loading,
	onClick,
	...props
}: {
	src: string;
	alt: string;
	caption?: string;
	loading?: boolean;
	onClick?: (attachmentId?: string) => void;
} & AttachmentFigureProps) {
	const openImageViewer = React.useCallback(() => {
		onClick?.(props.attachment?.id);
	}, [onClick, props.attachment]);

	return (
		<AttachmentFigure loading={loading} {...props}>
			<button className={styles.imageButton} disabled={loading} onClick={openImageViewer}>
				<img src={src} alt={alt} className={cx(styles.preview, styles.imagePreview)} />
				<AttachmentCaption text={caption} />
			</button>
		</AttachmentFigure>
	);
}

const ExternalLink = (props: React.HTMLProps<HTMLAnchorElement>) => (
	<a target="_blank" rel="noopener noreferrer" {...props} />
);

function FileAttachmentFigure({
	url,
	icon,
	caption,
	loading,
	...props
}: {
	url?: string;
	icon: IconDefinition;
	caption?: string;
	loading?: boolean;
} & AttachmentFigureProps) {
	let Container = loading ? "div" : ExternalLink;

	return (
		<AttachmentFigure loading={loading} {...props}>
			<Container
				className={styles.fileLink}
				// only pass href prop if we can actually link to the file
				{...(Container === ExternalLink && url != null ? { href: url } : undefined)}
			>
				<div className={cx(styles.preview, styles.filePreview)}>
					<FontAwesomeIcon icon={icon} className={styles.icon} />
				</div>
				<AttachmentCaption text={caption} />
			</Container>
		</AttachmentFigure>
	);
}

function UploadAttachmentFigure({ onClick, ...props }: { onClick: () => void } & AttachmentFigureProps) {
	return (
		<AttachmentFigure {...props}>
			<button onClick={onClick} className={styles.uploadButton}>
				<div className={cx(styles.preview, styles.uploadPreview)}>
					<FontAwesomeIcon icon={faPlusCircle} className={styles.plusIcon} />
				</div>
				<AttachmentCaption />
			</button>
		</AttachmentFigure>
	);
}

interface AttachmentDropzoneProps {
	attachments: FellowAttachment[];
	contentId: string;
	onChange?: () => void;
	previewCache: Map<string, string>;
	readonly?: boolean;
	upload?: (files: File[]) => void;
	unlink?: (id: string) => void;
	uploadingFiles?: { uuid: string; file: File }[];
}

type AttachmentDropzoneState = {
	draggingOver: boolean;
};

class AttachmentDropzone extends React.Component<AttachmentDropzoneProps, AttachmentDropzoneState> {
	state = {
		draggingOver: false,
	};
	dropzoneRef: React.RefObject<DropzoneRef> = React.createRef();
	lightbox?: {
		pswp: PhotoSwipe<PSUIOptions>;
		rootElement: HTMLDivElement;
	};

	componentDidUpdate(prevProps: AttachmentDropzoneProps) {
		if (!isEqual(prevProps.attachments, this.props.attachments)) {
			return this.props.onChange?.();
		}
	}

	onDrop = () => {
		this.setState({ draggingOver: false });
	};
	onDropAccepted = (files: File[]) => {
		const { upload } = this.props;
		return upload?.(files);
	};

	_onDragEnter = () => {
		// this.onDragLeave.cancel();
		this.setState({ draggingOver: true });
		return dispatchAnalyticsTrack("[1on1s] add attachment", {
			contentId: this.props.contentId,
			method: "drag and drop",
		});
	};
	onDragEnter = debounce(this._onDragEnter, 300, { maxWait: 300, leading: true });

	onDragLeave = () => {
		if (this.props.readonly) return;
		this.onDragEnter.cancel();
		this.setState({ draggingOver: false });
	};
	// onDragLeave = debounce(this._onDragLeave, 300, { maxWait: 300 });

	onClickUpload = () => {
		const { uploadingFiles, readonly } = this.props;
		if (readonly) return;

		if (!uploadingFiles?.length) {
			this.dropzoneRef.current?.open();
			return dispatchAnalyticsTrack("[1on1s] add attachment", {
				contentId: this.props.contentId,
				method: "click",
			});
		}
	};

	onClickRemove = memoize((attachment: FellowAttachment) => {
		if (this.props.readonly) return;
		return async () => {
			const res = await yesno(
				<div className={styles.removeWarning}>
					<p>Are you sure you want to remove this attachment?</p>
					<div className={styles.removeWarningContent}>
						<div className={styles.removeWarningPreview}>
							{attachment.filetype.startsWith("image") ? (
								<img src={attachment.url} className={cx(styles.preview, styles.imagePreview)} />
							) : (
								<div className={cx(styles.preview, styles.filePreview)}>
									<FontAwesomeIcon
										icon={fileTypeIcon(attachment.filetype)}
										className={styles.icon}
										fixedWidth
										size="2x"
									/>
								</div>
							)}
						</div>
						<div className={styles.removeWarningMeta}>
							<div className={styles.removeWarningFilename}>{attachment.filename}</div>
							{attachment.creator && (
								<div className={styles.removeWarningAddedBy}>
									Added by{" "}
									<span className={styles.removeWarningAddedByUser}>
										<SingleName
											user={{ ...attachment.creator, fullName: attachment.creator.fullName || "Unknown User" }}
										/>
									</span>
								</div>
							)}
						</div>
					</div>
				</div>,
				{
					title: "Remove Attachment",
					yesText: "Delete",
					noText: "Cancel",
					yesProps: { variant: "destructive" },
				},
			);
			if (!res) {
				return;
			}
			this.props.unlink?.(attachment.id);
			dispatchAnalyticsTrack("[1on1s] delete attachment", { contentId: this.props.contentId });
		};
	});

	addCaptionHTMLFn = (item: PhotoSwipe.Item, captionWrapperElement: Element) => {
		const caption = captionWrapperElement.getElementsByClassName("pswp__caption__center")[0] as HTMLDivElement;
		if (item.caption) {
			caption.innerHTML = item.caption;
		} else {
			caption.innerHTML = "";
		}
		return true;
	};

	afterChange = (pswp: PhotoSwipe<PSUIOptions>) => {
		const currentItem = pswp.currItem;
		const uiElement = pswp.container.nextElementSibling as HTMLDivElement;
		// set avatar and creator name
		if (currentItem.creator?.image && currentItem.creator?.fullName) {
			const imgEl = uiElement.querySelector("[data-pswp-avatar-image]") as HTMLImageElement;
			const nameEl = uiElement.querySelector("[data-pswp-creator-name]") as HTMLDivElement;
			imgEl.setAttribute("src", currentItem.creator?.image || "");
			nameEl.innerHTML = `Added by ${currentItem.creator?.fullName ?? ""}`;
		}
	};

	openImageViewer = async (attachmentId?: string) => {
		const imageItems: PhotoSwipe.Item[] = [];
		let initialIndex = 0;
		this.props.attachments.forEach(attachment => {
			if (attachment?.filetype.startsWith("image") && typeof attachment.url === "string") {
				imageItems.push({
					src: attachment.url,
					msrc: attachment.url, // TODO: smaller thumbnail urls
					attachmentId: typeof attachment.id === "string" ? attachment.id : undefined,
					...(attachment.dimensions
						? { w: attachment.dimensions.width, h: attachment.dimensions.height }
						: { autoSize: true }),
					caption: attachment.filename,
					creator: attachment.creator,
				});
				if (attachmentId === attachment.id) {
					initialIndex = imageItems.length - 1;
				}
			}
		});
		const getThumbBoundsFn = (index: number) => {
			const { attachmentId } = imageItems[index];
			if (attachmentId === undefined) return defaultThumbBounds();
			const thumb = document.querySelector(`${styles.attachmentWrapper} [data-attachment-id="${attachmentId}"]`);
			if (!thumb) return defaultThumbBounds();
			const rect = thumb.getBoundingClientRect();
			return {
				x: rect.left,
				y: rect.top,
				w: rect.width,
			};
		};
		try {
			const res = await openLightbox({
				items: imageItems,
				initialIndex,
				getThumbBoundsFn,
				addCaptionHTMLFn: this.addCaptionHTMLFn,
				onDestroy: this.onLightboxDestroyed,
				afterChange: this.afterChange,
			});
			if (res) {
				this.lightbox = {
					pswp: res?.pswp,
					rootElement: res?.rootElement,
				};
			}
		} catch (err) {
			console.error("Failed to open image viewer.", err);
		}
	};

	onLightboxDestroyed = () => {
		if (this.lightbox) {
			document.body.removeChild(this.lightbox.rootElement);
			delete this.lightbox;
		}
	};

	renderList = () => {
		const { attachments, readonly } = this.props;
		if (attachments.length || this.props.uploadingFiles?.length) {
			return (
				<ul className={styles.attachmentList}>
					{!isZoom && !readonly && <UploadAttachmentFigure onClick={this.onClickUpload} readonly={true} />}
					{this.props.uploadingFiles?.map(({ file, uuid }) => (
						<li key={uuid} className={styles.attachmentListItem} draggable={false}>
							{file.type.startsWith("image") ? (
								<ImageAttachmentFigure
									loading
									src={URL.createObjectURL(file)}
									alt="preview" /* FIXME */
									caption={file.name}
									readonly={readonly}
								/>
							) : (
								<FileAttachmentFigure loading icon={fileTypeIcon(file.type)} caption={file.name} readonly={readonly} />
							)}
						</li>
					))}
					{attachments.map(attachment => (
						<li key={attachment.id} className={styles.attachmentListItem}>
							{attachment.filetype.startsWith("image") ? (
								<ImageAttachmentFigure
									onDeleteClick={this.onClickRemove(attachment)}
									src={this.props.previewCache.get(attachment.id) ?? attachment.url}
									alt="preview" /* FIXME */
									caption={attachment.filename}
									attachment={attachment}
									readonly={readonly}
									onClick={this.openImageViewer}
								/>
							) : (
								<FileAttachmentFigure
									onDeleteClick={this.onClickRemove(attachment as FellowAttachment)}
									icon={fileTypeIcon(attachment.filetype)}
									caption={attachment.filename}
									url={attachment.url}
									attachment={attachment}
									readonly={readonly}
								/>
							)}
						</li>
					))}
				</ul>
			);
		}
		return null;
	};

	render() {
		return (
			<Dropzone
				ref={this.dropzoneRef}
				disabled={!this.props.readonly && !!this.props.uploadingFiles?.length}
				multiple
				onDrop={this.onDrop}
				onDropAccepted={this.onDropAccepted}
				onDragEnter={this.onDragEnter}
				onDragLeave={this.onDragLeave}
			>
				{({ getInputProps, getRootProps }) => {
					return (
						<div
							{...getRootProps()}
							className={cx(styles.attachmentWrapper, { [styles.draggingOver]: this.state.draggingOver })}
							onClick={noop}
						>
							<input {...getInputProps()} />
							{this.renderList()}
						</div>
					);
				}}
			</Dropzone>
		);
	}
}

export default AttachmentDropzone;
